import { MINUTES_REMAINING_WARN_DAYS_LEFT } from '../constants'

export interface BillingTheme {
  surface: string
  label: string
  heading: string
  body: string
  trackBg: string
  trackFill: string
  axis: string
}

export const ORANGE_THEME: BillingTheme = {
  surface: 'bg-orange-50 border-orange-200',
  label: 'text-orange-700',
  heading: 'text-orange-900',
  body: 'text-orange-800',
  trackBg: 'bg-orange-100',
  trackFill: 'bg-orange-500',
  axis: 'text-orange-700',
}

// Danger theme for an over-budget (negative) balance — more severe than the
// "running low" orange. Uses the project's semantic error ramp.
export const RED_THEME: BillingTheme = {
  surface: 'bg-error-subtle-bg border-error-subtle-border',
  label: 'text-error-low-contrast',
  heading: 'text-error-low-contrast',
  body: 'text-error-low-contrast',
  trackBg: 'bg-error-hover-bg',
  trackFill: 'bg-error-solid',
  axis: 'text-error-low-contrast',
}

export const GREEN_THEME: BillingTheme = {
  surface: 'bg-green-50 border-green-200',
  label: 'text-green-700',
  heading: 'text-green-900',
  body: 'text-green-800',
  trackBg: 'bg-green-100',
  trackFill: 'bg-green-500',
  axis: 'text-green-700',
}

// Neutral theme for a closed/past period: the numbers are final, so the page
// should read as informational — not the orange "running low" warning tone.
export const BLUE_THEME: BillingTheme = {
  surface: 'bg-blue-50 border-blue-200',
  label: 'text-blue-700',
  heading: 'text-blue-900',
  body: 'text-blue-800',
  trackBg: 'bg-blue-100',
  trackFill: 'bg-blue-500',
  axis: 'text-blue-700',
}

/** Resolve the theme used across the Billing page based on whether we're
 * looking at the current period, the remaining balance, and how the forecast
 * looks. Closed periods are neutral (blue); the orange/red tones are reserved
 * for the current period running low or over budget. */
export function resolveBillingTheme(
  isCurrent: boolean,
  forecast: { daysRemaining: number } | null,
  remainingMinutes?: number
): BillingTheme {
  if (!isCurrent) return BLUE_THEME
  // Overage: a negative balance is a danger state, not a healthy (green) one.
  if (remainingMinutes != null && remainingMinutes < 0) return RED_THEME
  return forecast && forecast.daysRemaining < MINUTES_REMAINING_WARN_DAYS_LEFT
    ? ORANGE_THEME
    : GREEN_THEME
}
